import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Initialize from localStorage immediately
    const storedUser = localStorage.getItem('arrise_user');
    const token = localStorage.getItem('arrise_token');
    if (storedUser && token) {
      try {
        return JSON.parse(storedUser);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('arrise_token');
      const storedUser = localStorage.getItem('arrise_user');
      
      if (token && storedUser) {
        try {
          // Verify token is still valid in background
          const response = await authAPI.getMe();
          setUser(response.data);
          localStorage.setItem('arrise_user', JSON.stringify(response.data));
        } catch (error) {
          // Only clear if it's a 401 error
          if (error.response?.status === 401) {
            console.error('Token expired:', error);
            localStorage.removeItem('arrise_token');
            localStorage.removeItem('arrise_user');
            setUser(null);
          }
          // For other errors, keep the user logged in with stored data
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    const { token, user: userData } = response.data;
    
    localStorage.setItem('arrise_token', token);
    localStorage.setItem('arrise_user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { token, user: userData } = response.data;
    
    localStorage.setItem('arrise_token', token);
    localStorage.setItem('arrise_user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('arrise_token');
    localStorage.removeItem('arrise_user');
    setUser(null);
  };

  const isAdmin = () => user?.role === 'admin';
  const isManager = () => user?.role === 'manager' || user?.role === 'admin';
  const isLead = () => user?.role === 'lead' || isManager();
  const isArtist = () => user?.role === 'artist';
  const canViewPerformance = () => !isArtist();

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAdmin,
    isManager,
    isLead,
    isArtist,
    canViewPerformance,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
